import React, { useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Row,
  Col,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { Vietnamese } from "flatpickr/dist/l10n/vn";
import { english } from "flatpickr/dist/l10n/default";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { auditLogTypes, auditLogActions, initOptionDateLocale } from "./utils";
import { convertDateTime, formatDateTime } from "helpers/utils";

const ExportAuditLogsModal = ({ isOpen, toggle, onExport }) => {
  const { t, i18n } = useTranslation(["auditLogs"]);

  const [optionDateLocale, setOptionDateLocale] = useState({
    ...initOptionDateLocale,
  });

  React.useEffect(() => {
    setOptionDateLocale({
      ...initOptionDateLocale,
      locale: i18n.language === "vn" ? Vietnamese : english,
    });
  }, [i18n.language]);

  const schema = yup.object().shape({
    type: yup.object().nullable(),
    date: yup.date().nullable(),
    action: yup.object().nullable(),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      type: { value: null, label: t("form.all") },
      date: null,
      action: { value: null, label: t("form.all") },
    },
  });

  const typeOptions = useMemo(() => {
    return [
      { value: null, label: t("form.all") },
      ...auditLogTypes.map(item => ({
        value: item.key,
        label: item.label,
      })),
    ];
  }, [t]);

  const actionOptions = useMemo(() => {
    return [
      { value: null, label: t("form.all") },
      ...auditLogActions.map(item => ({
        value: item.key,
        label: item.label,
      })),
    ];
  }, [t]);

  const onSubmit = values => {
    const columnFilter = {};

    if (values.type?.value) {
      columnFilter["filter[type]"] = values.type.value;
    }
    if (values.action?.value) {
      columnFilter["filter[action]"] = values.action.value;
    }
    if (values.date) {
      columnFilter["filter[maker_date]"] = convertDateTime(
        values.date,
        formatDateTime.YYYY_MM_DD,
        "-"
      );
    }

    const exportParams = {
      page: 1,
      sizePerPage: 10,
      sort: "-maker_date",
      keywords: "",
      fields: "",
      expand: "",
      columnFilter,
    };

    onExport(exportParams);
    handleClose();
  };

  const handleClose = () => {
    reset({
      type: { value: null, label: t("form.all") },
      date: null,
      action: { value: null, label: t("form.all") },
    });
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md" centered>
      <ModalHeader toggle={handleClose}>{t("export_modal.title")}</ModalHeader>
      <ModalBody className="p-3">
        <Row>
          <Col xs={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="type">{t("table.type")}</Label>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("table.type"),
                    })}
                    options={typeOptions}
                    classNamePrefix="select2-selection"
                  />
                )}
              />
              {errors.type && (
                <MessageFormValidate error={errors.type.message} />
              )}
            </FormGroup>
          </Col>

          <Col xs={12}>
            <FormGroup className="mb-3">
              <Label htmlFor="date">{t("form.date")}</Label>
              <Controller
                control={control}
                name="date"
                render={({ field }) => (
                  <Flatpickr
                    {...field}
                    className="form-control d-block Flatpickr__Input"
                    placeholder="DD/MM/YYYY"
                    options={{
                      ...optionDateLocale,
                      mode: "single",
                    }}
                  />
                )}
              />
              {errors.date && (
                <MessageFormValidate error={errors.date.message} />
              )}
            </FormGroup>
          </Col>

          <Col xs={12}>
            <FormGroup className="mb-0">
              <Label htmlFor="action">{t("table.action")}</Label>
              <Controller
                control={control}
                name="action"
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("placeholder.select", {
                      title: t("table.action"),
                    })}
                    options={actionOptions}
                    classNamePrefix="select2-selection"
                  />
                )}
              />
              {errors.action && (
                <MessageFormValidate error={errors.action.message} />
              )}
            </FormGroup>
            <small className="form-text text-danger ">
              <i>{t("form.date_note")}</i>
            </small>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className="p-2">
        <Button color="secondary" onClick={handleClose}>
          {t("button.close")}
        </Button>
        <Button color="primary" onClick={handleSubmit(onSubmit)}>
          <i className="fas fa-file-export me-2"></i>
          {t("button.export")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExportAuditLogsModal;
